"use client";

import { Calendar, ChevronLeft, Clock, Share2, User } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

declare const wvcClient: any;

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="h-12 bg-muted/30 rounded-lg animate-pulse w-3/4" />
        <div className="h-6 bg-muted/30 rounded-lg animate-pulse w-1/2" />
        <div className="h-[400px] bg-muted/30 rounded-lg animate-pulse" />
        <div className="space-y-3">
          <div className="h-4 bg-muted/30 rounded animate-pulse" />
          <div className="h-4 bg-muted/30 rounded animate-pulse w-5/6" />
          <div className="h-4 bg-muted/30 rounded animate-pulse w-4/6" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto text-center py-20">
        <p className="text-muted-foreground">Unable to load this insight.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="max-w-4xl mx-auto text-center py-20">
        <p className="text-muted-foreground">No insights available.</p>
      </div>
    );
  }

  const post = posts[0];
  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  const authorName = post.author?.name || "Catalyst Strategy Team";
  const authorAvatar =
    post.author?.avatar?.["96"] ||
    post.author?.avatar?.["48"] ||
    post.author?.avatar?.["24"] ||
    "";

  return (
    <article className="max-w-4xl mx-auto">
      <div className="mb-8">
        <Link to="/TheLabPage">
          <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-primary">
            <ChevronLeft className="size-4" />
            Back to The Lab
          </Button>
        </Link>
      </div>

      {post.categories && post.categories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6">
          {post.categories.map((cat, i) => (
            <span
              key={cat.id}
              data-index={i}
              className="text-xs uppercase tracking-widest text-primary font-bold px-3 py-1 rounded-md bg-primary/10 border border-primary/20"
            >
              {cat.name}
            </span>
          ))}
        </div>
      )}

      <h1
        className="font-default text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6 text-foreground"
        dangerouslySetInnerHTML={{ __html: post.title }}
      />

      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-muted-foreground mb-10">
        <div className="flex items-center gap-2">
          <User className="size-4" />
          <span>{authorName}</span>
        </div>
        {formattedDate && (
          <div className="flex items-center gap-2">
            <Calendar className="size-4" />
            <span>{formattedDate}</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <Clock className="size-4" />
          <span>5 min read</span>
        </div>
      </div>

      {post.featuredImage && (
        <div className="mb-12 rounded-lg overflow-hidden shadow-lg">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-auto object-cover"
            loading="lazy"
            decoding="async"
          />
        </div>
      )}

      {post.content && (
        <div
          className="prose prose-invert max-w-none font-default text-base md:text-lg leading-relaxed text-foreground/90 [&_h2]:font-bold [&_h2]:text-2xl md:[&_h2]:text-3xl [&_h2]:mt-12 [&_h2]:mb-4 [&_h2]:text-foreground [&_h3]:font-bold [&_h3]:text-xl md:[&_h3]:text-2xl [&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:text-foreground [&_p]:mb-6 [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4 hover:[&_a]:text-primary/80 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-6 [&_li]:mb-2 [&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:pl-6 [&_blockquote]:italic [&_blockquote]:text-muted-foreground [&_blockquote]:my-8 [&_img]:rounded-lg [&_img]:my-8 [&_strong]:text-foreground [&_strong]:font-bold"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      )}

      {post.tags && post.tags.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mt-12 pt-8 border-t border-border">
          <span className="text-sm text-muted-foreground mr-2">Tags:</span>
          {post.tags.map((tag, i) => (
            <span
              key={tag.id}
              data-index={i}
              className="text-xs px-3 py-1 rounded-md bg-secondary text-secondary-foreground border border-border"
            >
              {tag.name}
            </span>
          ))}
        </div>
      )}

      <Separator className="my-12" />

      <Card className="bg-card border border-border shadow-md">
        <CardContent className="p-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <Avatar className="size-20 rounded-lg border-2 border-primary/20">
              {authorAvatar && <AvatarImage src={authorAvatar} alt={authorName} />}
              <AvatarFallback className="bg-primary/10 text-primary font-bold text-2xl rounded-lg">
                {authorName.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <p className="text-xs uppercase tracking-widest text-primary mb-2">Author</p>
              <h3 className="font-default text-2xl font-bold mb-2 text-foreground">
                {authorName}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Lead strategist at Catalyst Growth Lab, specializing in high-impact digital transformation and scalable revenue growth.
              </p>
            </div>
            <Button variant="outline" size="sm" className="shrink-0">
              <Share2 className="size-4" />
              Share
            </Button>
          </div>
        </CardContent>
      </Card>
    </article>
  );
}

function RelatedPostsContent() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[...Array(3)].map((_, i) => (
          <div key={i} data-index={i} className="h-80 rounded-lg bg-muted/30 animate-pulse" />
        ))}
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <p className="text-center text-muted-foreground">No related insights available.</p>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {posts.slice(0, 3).map((post, i) => {
        const dateStr = post.date
          ? new Date(post.date).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })
          : "";

        return (
          <Link
            key={post.id}
            data-index={i}
            to={post.link || "#"}
            className="group block"
          >
            <Card className="h-full overflow-hidden bg-card border border-border hover:border-primary/40 transition-all duration-300 shadow-md hover:shadow-lg">
              {post.featuredImage && (
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={post.featuredImage}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              )}
              <CardContent className="p-6">
                {post.categories && post.categories.length > 0 && (
                  <p className="text-xs uppercase tracking-widest text-primary mb-3">
                    {post.categories[0].name}
                  </p>
                )}
                <h3
                  className="font-default text-xl font-bold leading-snug mb-3 text-foreground group-hover:text-primary transition-colors line-clamp-2"
                  dangerouslySetInnerHTML={{ __html: post.title }}
                />
                {dateStr && (
                  <p className="text-sm text-muted-foreground">{dateStr}</p>
                )}
              </CardContent>
            </Card>
          </Link>
        );
      })}
    </div>
  );
}

export default function SinglePostTemplateSection() {
  const currentWPQueryParams =
    typeof window !== "undefined" && (window as any).wvcClient
      ? (window as any).wvcClient.getCurrentWPQueryParams()
      : { post_type: "post" };

  const singlePostQuery = new WP_Query(currentWPQueryParams);
  const relatedPostsQuery = new WP_Query({
    post_type: currentWPQueryParams?.post_type || "post",
    per_page: 3,
  });

  return (
    <section data-section-id="4331"
      id="single-post-template-section"
      className="bg-background text-foreground py-16 md:py-24 px-4 md:px-8"
    >
      <div className="container mx-auto">
        <PostsProvider wp_query={singlePostQuery}>
          <SinglePostContent />
        </PostsProvider>

        <div className="max-w-6xl mx-auto mt-24">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <p className="text-xs uppercase tracking-widest text-primary mb-2">
                Explore More
              </p>
              <h2 className="font-default text-3xl md:text-4xl font-bold text-foreground">
                Related Growth Insights
              </h2>
            </div>
            <Link to="/TheLabPage">
              <Button variant="outline" size="sm">
                View All
              </Button>
            </Link>
          </div>

          <PostsProvider wp_query={relatedPostsQuery}>
            <RelatedPostsContent />
          </PostsProvider>
        </div>
      </div>
    </section>
  );
}